import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getSEO } from './api';
import { useSiteInfo } from './settingsContext';

const DEFAULTS = {
  '/': {
    title: 'Apex Quality Control — Quality You Can Trust. Excellence We Deliver.',
    description: 'Independent inspection, testing and audit services. Rooted in Somalia, working globally. Reports within 24h.',
    keywords: 'quality control, inspection, testing, vendor audit, cargo survey, Somalia, third-party inspection',
  },
  '/about': {
    title: 'About AQC — Independent Quality Assurance | Apex Quality Control',
    description: 'AQC is an independent inspection and audit company — rooted in Somalia, working globally across 10 countries and 8 industries.',
    keywords: 'about apex quality control, aqc somalia, independent inspection',
  },
  '/services': {
    title: 'Services — Inspection, Testing, Vendor Audit, Cargo Survey | AQC',
    description: 'Quality Inspection, Product Testing, Vendor Audit, Cargo Survey & Loading. Four core capabilities. Twelve service types. Reports within 24h.',
    keywords: 'quality inspection, product testing, vendor audit, cargo survey, AQL sampling, ISO 2859',
  },
  '/industries': {
    title: 'Industries We Serve | Apex Quality Control',
    description: 'AQC covers food, textiles, construction, medical, livestock, electronics, industrial equipment, and minerals/oil/gas sectors.',
    keywords: 'industries served, sector expertise, food safety, textile QC, medical devices',
  },
  '/standards': {
    title: 'Standards & Certifications — ISO, ASTM, EN, HACCP, SA8000 | AQC',
    description: 'AQC applies ISO 17020, 17025, 2859-1, 9001, 13485, ASTM F963, EN 71, CE/FCC/RoHS, HACCP and SA8000 in its inspection and audit work.',
    keywords: 'ISO 17020, ISO 17025, ASTM, EN, HACCP, SA8000, CE, RoHS, quality standards',
  },
  '/why-aqc': {
    title: 'Why AQC — Independent, Fast, Global | Apex Quality Control',
    description: 'Truly independent, on-site within 72 hours, reports within 24 hours. One coordinator, online. Aligned to international standards.',
    keywords: 'why choose aqc, independent inspection, 24h reports, global coverage',
  },
  '/resources': {
    title: 'Resources & Downloads | Apex Quality Control',
    description: 'Download the AQC company profile, construction QC checklist and other resources.',
    keywords: 'aqc company profile, construction QC checklist, downloads',
  },
  '/contact': {
    title: 'Contact AQC — Request a Quote | Apex Quality Control',
    description: 'Contact us for inspection, testing, auditing, certification, and quality-control services. We respond within one business day.',
    keywords: 'contact aqc, request a quote, inspection quote',
  },
  '/privacy': { title: 'Privacy Policy | Apex Quality Control', description: 'How we handle and protect your personal data at Apex Quality Control.' },
  '/cookies': { title: 'Cookie Policy | Apex Quality Control', description: 'How we use cookies at Apex Quality Control.' },
  '/terms': { title: 'Terms of Use | Apex Quality Control', description: 'Terms and conditions for using the Apex Quality Control website.' },
};

const SITE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://apex-exact-rebuild.preview.emergentagent.com';

function serviceTitle(slug) {
  const s = slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return `${s} | Apex Quality Control`;
}

export default function SEO({
  title,
  description,
  keywords,
  ogImage,
  jsonLd,
  path,
}) {
  const location = useLocation();
  const site = useSiteInfo();
  const currentPath = path || location.pathname || '/';
  const [override, setOverride] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getSEO(currentPath);
        if (alive && data && (data.title || data.description)) setOverride(data);
      } catch {}
    })();
    return () => { alive = false; };
  }, [currentPath]);

  let fallback = DEFAULTS[currentPath] || {};
  if (!fallback.title && currentPath.startsWith('/services/')) {
    const slug = currentPath.replace('/services/', '');
    fallback = { title: serviceTitle(slug), description: 'Independent verification and reports within 24 hours.', keywords: `${slug.replace(/-/g, ' ')}, inspection, AQC` };
  }

  const finalTitle = override?.title || title || fallback.title || 'Apex Quality Control';
  const finalDesc = override?.description || description || fallback.description || 'Independent inspection, testing and audit services.';
  const finalKeywords = override?.keywords || keywords || fallback.keywords || '';
  const finalOg = override?.og_image || ogImage || `${SITE_URL}/og-image.png`;
  const finalCanonical = override?.canonical || `${SITE_URL}${currentPath}`;

  const organizationLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: site.name,
    url: SITE_URL,
    email: site.email,
    telephone: site.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: `${site.address?.line1 || ''} ${site.address?.line2 || ''}`.trim(),
      addressLocality: site.address?.city,
      addressCountry: site.address?.country,
    },
    sameAs: [site.socials?.linkedin, site.socials?.twitter, site.socials?.facebook].filter(Boolean).filter((s) => s !== '#'),
  };

  const websiteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: SITE_URL,
    name: site.name,
    publisher: { '@type': 'Organization', name: site.name },
  };

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <link rel="canonical" href={finalCanonical} />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      {finalOg && <meta property="og:image" content={finalOg} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      {finalOg && <meta name="twitter:image" content={finalOg} />}

      <script type="application/ld+json">{JSON.stringify(organizationLd)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteLd)}</script>
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}

export function BreadcrumbLd({ items }) {
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.url?.startsWith('http') ? it.url : `${SITE_URL}${it.url}`,
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(ld)}</script>
    </Helmet>
  );
}

export function ServiceLd({ name, description, provider, image }) {
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    provider: { '@type': 'Organization', name: provider || 'Apex Quality Control' },
    ...(image && { image }),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(ld)}</script>
    </Helmet>
  );
}
